import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Typography } from '@mui/material';
import { getAppStoreById, getPosts } from '@/api/appstore.api';
import type { BaseModalProps } from '@/interfaces/component/modal.interface';
import { type AppStorePostFilter, type AppStorePostSortOption } from '@/interfaces/service/appstore/appstore.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import { setRequestPostParams } from '@/services/appstore.service';
import { REVIEW_ROUTES } from '@/constants/path.constants';

const usePollingGetReviewPosts = (onCrawlingAppStoreId: number) => {
  const router = useRouter();

  const { appStores, setAppStores, popOnCrawlingAppStoreId } = useAppStoreStore((state) => state);

  const [showCrawlingNoticeModal, setShowCrawlingNoticeModal] = useState(false);
  const [crawlingNoticeModalProps, setCrawlingNoticeModalProps] = useState<Partial<BaseModalProps>>();
  const [showGoToListPageButton, setShowGoToListPageButton] = useState(false);

  const gotoReviewsPage = () => router.push(REVIEW_ROUTES.getReviews(onCrawlingAppStoreId));

  useEffect(() => {
    const { status, postData } = appStores[onCrawlingAppStoreId] ?? {};
    const posts = postData?.posts ?? [];

    if (status === 'STABLE' && posts.length > 0) {
      popOnCrawlingAppStoreId(onCrawlingAppStoreId);
      return;
    }

    let pollingData;

    if (status !== 'STABLE' || posts.length === 0) {
      pollingData = setInterval(async () => {
        const res = await getAppStoreById(onCrawlingAppStoreId);
        if (res) {
          const baseFilter: AppStorePostFilter = {
            channelFilter: 'ALL',
            replyFilter: 'ALL'
          };
          const baseSortOption: AppStorePostSortOption = 'LATEST';

          const _onCrawlingAppStore = {
            ...res,
            filter: baseFilter,
            sortOption: baseSortOption
          };

          const params = setRequestPostParams(_onCrawlingAppStore);

          const postsCountRes = (await getPosts(params)) ?? { totalCount: 0 };
          const totalCount = postsCountRes.totalCount ?? 0;

          if (res.status === 'STABLE' || totalCount > 0) {
            clearInterval(pollingData);

            setAppStores([_onCrawlingAppStore], onCrawlingAppStoreId);

            if (totalCount > 0) {
              setCrawlingNoticeModalProps({
                children: (
                  <Typography color="textSecondary" align="center" variant="subtitle1">
                    이제
                    {` ${_onCrawlingAppStore.storeName ?? '리뷰 수집을 요청하신 병원'}`}
                    의 리뷰를
                    <br />
                    확인할 수 있어요.
                  </Typography>
                ),
                closeButtonTitle: '리뷰 확인하러 가기',
                onCloseModal: () => {
                  gotoReviewsPage();
                  popOnCrawlingAppStoreId(onCrawlingAppStoreId);
                }
              });
              setShowGoToListPageButton(true);
              setShowCrawlingNoticeModal(true);
            } else if (res.status === 'STABLE') {
              window.location.reload();
            }
          }
        }
      }, 2000);
    }

    return () => clearInterval(pollingData);
  }, []);

  return {
    showCrawlingNoticeModal,
    toggleShowCrawlingNoticeModal: () => setShowCrawlingNoticeModal(!showCrawlingNoticeModal),
    crawlingNoticeModalProps,
    showGoToListPageButton,
    gotoReviewsPage
  };
};

export default usePollingGetReviewPosts;
